import { genFocusStyle, resetComponent } from '../../style';
import { initFadeMotion, initZoomMotion } from '../../style/motion';
import { genStyleHooks, mergeToken } from '../../theme/internal';
import { unit } from '@ant-design/cssinjs';
function box(position) {
  return {
    position,
    inset: 0
  };
}
export const genModalMaskStyle = token => {
  const {
    componentCls,
    antCls
  } = token;
  return [{
    ["".concat(componentCls, "-root")]: {
      ["".concat(componentCls).concat(antCls, "-zoom-enter, ").concat(componentCls).concat(antCls, "-zoom-appear")]: {
        // reset scale avoid mousePosition bug
        transform: 'none',
        opacity: 0,
        animationDuration: token.motionDurationSlow,
        // https://github.com/ant-design/ant-design/issues/11777
        userSelect: 'none'
      },
      // https://github.com/ant-design/ant-design/issues/37329
      // https://github.com/ant-design/ant-design/issues/40272
      ["".concat(componentCls).concat(antCls, "-zoom-leave ").concat(componentCls, "-content")]: {
        pointerEvents: 'none'
      },
      ["".concat(componentCls, "-mask")]: Object.assign(Object.assign({}, box('fixed')), {
        zIndex: token.zIndexPopupBase,
        height: '100%',
        backgroundColor: token.colorBgMask,
        pointerEvents: 'none',
        ["".concat(componentCls, "-hidden")]: {
          display: 'none'
        }
      }),
      ["".concat(componentCls, "-wrap")]: Object.assign(Object.assign({}, box('fixed')), {
        zIndex: token.zIndexPopupBase,
        overflow: 'auto',
        outline: 0,
        WebkitOverflowScrolling: 'touch'
      })
    }
  }, {
    ["".concat(componentCls, "-root")]: initFadeMotion(token)
  }];
};
const genModalStyle = token => {
  const {
    componentCls
  } = token;
  return [
  // ======================== Root =========================
  {
    ["".concat(componentCls, "-root")]: {
      ["".concat(componentCls, "-wrap-rtl")]: {
        direction: 'rtl'
      },
      ["".concat(componentCls, "-centered")]: {
        textAlign: 'center',
        '&::before': {
          display: 'inline-block',
          width: 0,
          height: '100%',
          verticalAlign: 'middle',
          content: '""'
        },
        [componentCls]: {
          top: 0,
          display: 'inline-block',
          paddingBottom: 0,
          textAlign: 'start',
          verticalAlign: 'middle'
        }
      },
      ["@media (max-width: ".concat(token.screenSMMax, "px)")]: {
        [componentCls]: {
          maxWidth: 'calc(100vw - 16px)',
          margin: "".concat(unit(token.marginXS), " auto")
        },
        ["".concat(componentCls, "-centered")]: {
          [componentCls]: {
            flex: 1
          }
        }
      }
    }
  },
  // ======================== Modal ========================
  {
    [componentCls]: Object.assign(Object.assign({}, resetComponent(token)), {
      pointerEvents: 'none',
      position: 'relative',
      top: 100,
      width: 'auto',
      maxWidth: "calc(100vw - ".concat(unit(token.calc(token.margin).mul(2).equal()), ")"),
      margin: '0 auto',
      paddingBottom: token.paddingLG,
      ["".concat(componentCls, "-title")]: {
        margin: 0,
        color: token.titleColor,
        fontWeight: token.fontWeightStrong,
        fontSize: token.titleFontSize,
        lineHeight: token.titleLineHeight,
        wordWrap: 'break-word'
      },
      ["".concat(componentCls, "-content")]: {
        position: 'relative',
        backgroundColor: token.contentBg,
        backgroundClip: 'padding-box',
        border: 0,
        borderRadius: token.borderRadiusLG,
        boxShadow: token.boxShadow,
        pointerEvents: 'auto',
        padding: token.contentPadding
      },
      ["".concat(componentCls, "-close")]: Object.assign({
        position: 'absolute',
        top: token.calc(token.modalHeaderHeight).sub(token.modalCloseBtnSize).div(2).equal(),
        insetInlineEnd: token.calc(token.modalHeaderHeight).sub(token.modalCloseBtnSize).div(2).equal(),
        zIndex: token.calc(token.zIndexPopupBase).add(10).equal(),
        padding: 0,
        color: token.modalCloseIconColor,
        fontWeight: token.fontWeightStrong,
        lineHeight: 1,
        textDecoration: 'none',
        background: 'transparent',
        borderRadius: token.borderRadiusSM,
        width: token.modalCloseBtnSize,
        height: token.modalCloseBtnSize,
        border: 0,
        outline: 0,
        cursor: 'pointer',
        transition: "color ".concat(token.motionDurationMid, ", background-color ").concat(token.motionDurationMid),
        '&-x': {
          display: 'flex',
          fontSize: token.fontSizeLG,
          fontStyle: 'normal',
          lineHeight: "".concat(unit(token.modalCloseBtnSize)),
          justifyContent: 'center',
          textTransform: 'none',
          textRendering: 'auto'
        },
        '&:hover': {
          color: token.modalCloseIconHoverColor,
          backgroundColor: token.colorBgTextHover,
          textDecoration: 'none'
        },
        '&:active': {
          backgroundColor: token.colorBgTextActive
        }
      }, genFocusStyle(token)),
      ["".concat(componentCls, "-header")]: {
        color: token.colorText,
        background: token.headerBg,
        borderRadius: "".concat(unit(token.borderRadiusLG), " ").concat(unit(token.borderRadiusLG), " 0 0"),
        marginBottom: token.headerMarginBottom,
        padding: token.headerPadding,
        borderBottom: token.headerBorderBottom
      },
      ["".concat(componentCls, "-body")]: {
        fontSize: token.fontSize,
        lineHeight: token.lineHeight,
        wordWrap: 'break-word',
        padding: token.bodyPadding
      },
      ["".concat(componentCls, "-footer")]: {
        textAlign: 'end',
        background: token.footerBg,
        marginTop: token.footerMarginTop,
        padding: token.footerPadding,
        borderTop: token.footerBorderTop,
        borderRadius: token.footerBorderRadius,
        ["> ".concat(token.antCls, "-btn + ").concat(token.antCls, "-btn")]: {
          marginInlineStart: token.marginXS
        }
      },
      ["".concat(componentCls, "-open")]: {
        overflow: 'hidden'
      }
    })
  },
  // ======================== Pure =========================
  {
    ["".concat(componentCls, "-pure-panel")]: {
      top: 'auto',
      padding: 0,
      display: 'flex',
      flexDirection: 'column',
      ["".concat(componentCls, "-content,\n          ").concat(componentCls, "-body,\n          ").concat(componentCls, "-confirm-body-wrapper")]: {
        display: 'flex',
        flexDirection: 'column',
        flex: 'auto'
      },
      ["".concat(componentCls, "-confirm-body")]: {
        marginBottom: 'auto'
      }
    }
  }];
};
const genRTLStyle = token => {
  const {
    componentCls
  } = token;
  return {
    ["".concat(componentCls, "-root")]: {
      ["".concat(componentCls, "-wrap-rtl")]: {
        direction: 'rtl',
        ["".concat(componentCls, "-confirm-body")]: {
          direction: 'rtl'
        }
      }
    }
  };
};
// ============================== Export ==============================
export const prepareToken = token => {
  const headerPaddingVertical = token.padding;
  const headerFontSize = token.fontSizeHeading5;
  const headerLineHeight = token.lineHeightHeading5;
  const modalToken = mergeToken(token, {
    modalHeaderHeight: token.calc(token.calc(headerLineHeight).mul(headerFontSize).equal()).add(token.calc(headerPaddingVertical).mul(2).equal()).equal(),
    modalFooterBorderColorSplit: token.colorSplit,
    modalFooterBorderStyle: token.lineType,
    modalFooterBorderWidth: token.lineWidth,
    modalCloseIconColor: token.colorIcon,
    modalCloseIconHoverColor: token.colorIconHover,
    modalCloseBtnSize: token.controlHeight,
    modalConfirmIconSize: token.fontHeight,
    modalTitleHeight: token.calc(token.titleFontSize).mul(token.titleLineHeight).equal()
  });
  return modalToken;
};
export const prepareComponentToken = token => ({
  footerBg: 'transparent',
  headerBg: token.colorBgElevated,
  titleLineHeight: token.lineHeightHeading5,
  titleFontSize: token.fontSizeHeading5,
  contentBg: token.colorBgElevated,
  titleColor: token.colorTextHeading,
  // internal
  contentPadding: token.wireframe ? 0 : "".concat(unit(token.paddingMD), " ").concat(unit(token.paddingContentHorizontalLG)),
  headerPadding: token.wireframe ? "".concat(unit(token.padding), " ").concat(unit(token.paddingLG)) : 0,
  headerBorderBottom: token.wireframe ? "".concat(unit(token.lineWidth), " ").concat(token.lineType, " ").concat(token.colorSplit) : 'none',
  headerMarginBottom: token.wireframe ? 0 : token.marginXS,
  bodyPadding: token.wireframe ? token.paddingLG : 0,
  footerPadding: token.wireframe ? "".concat(unit(token.paddingXS), " ").concat(unit(token.padding)) : 0,
  footerBorderTop: token.wireframe ? "".concat(unit(token.lineWidth), " ").concat(token.lineType, " ").concat(token.colorSplit) : 'none',
  footerBorderRadius: token.wireframe ? "0 0 ".concat(unit(token.borderRadiusLG), " ").concat(unit(token.borderRadiusLG)) : 0,
  footerMarginTop: token.wireframe ? 0 : token.marginSM,
  confirmBodyPadding: token.wireframe ? "".concat(unit(token.padding * 2), " ").concat(unit(token.padding * 2), " ").concat(unit(token.paddingLG)) : 0,
  confirmIconMarginInlineEnd: token.wireframe ? token.margin : token.marginSM,
  confirmBtnsMarginTop: token.wireframe ? token.marginLG : token.marginSM
});
export default genStyleHooks('Modal', token => {
  const modalToken = prepareToken(token);
  return [genModalStyle(modalToken), genRTLStyle(modalToken), genModalMaskStyle(modalToken), initZoomMotion(modalToken, 'zoom')];
}, prepareComponentToken, {
  unitless: {
    titleLineHeight: true
  }
});